/* eslint-disable */
import { lazy, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';

import { jwtDecode } from 'jwt-decode';
import PropTypes from 'prop-types';
import Password from '../components/Form/Password';
import { PatrolTaskProvider } from '../context/PatrolTaskContext';
import { PatrolReportProvider } from '../context/PatrolReportContext';

const Main = lazy(() => import('../screens/Main'));

const HomePageV2 = lazy(() => import('../screens/Main/HomePageV2'));
const NotificationSettings = lazy(() => import('../screens/Main/NotificationSettings'));
const SearchResultsList = lazy(() => import('../screens/Main/SearchResultsList'));
const AdminUsers = lazy(() => import('../screens/Main/AdminUsers'));
const AdminDeactivateUser = lazy(() => import('../screens/Main/AdminDeactivateUser'));
const RestoreAccount = lazy(() => import('../screens/Main/RestoreAccount'));
const AdminAgencies = lazy(() => import('../screens/Main/AdminAgencies'));
const AdminLocation = lazy(() => import('../screens/Main/AdminLocation'));
const AdminPartyTypes = lazy(() => import('../screens/Main/AdminPartyTypes'));
const CaseType = lazy(() => import('../screens/Main/AdminCaseType'));
const Parties = lazy(() => import('../screens/Main/AdminParties'));
const AccountRegistration = lazy(() => import('../screens/Main/AccountRegistration'));
const Expense = lazy(() => import('../screens/Main/Expense'));
const TimeEntries = lazy(() => import('../screens/Main/TimeEntries'));
const RejectedTimeEntries = lazy(() => import('../screens/Main/TimeEntries/RejectedTimeEntries'));
const RoleManagement = lazy(() => import('../screens/Main/RoleManagement'));
const AddCustomRole = lazy(() => import('../screens/Main/RoleManagement/AddCustomRole'));
const SystemRoleDetail = lazy(() => import('../screens/Main/RoleManagement/SystemRoleDetail'));
const CustomRoleDetail = lazy(() => import('../screens/Main/RoleManagement/CustomRoleDetail'));
const EditRoleDetail = lazy(() => import('../screens/Main/RoleManagement/EditRoleDetail'));
const BillingPeriod = lazy(() => import('../screens/Main/BillingPeriod/BillingPeriod'));
const Signup = lazy(() => import('../screens/Auth/SignUp'));
const NotificationList = lazy(() => import('../components/common/NotificationsList'));
const PostManagementScreen = lazy(() => import('../screens/Main/PostManagementScreen'));
const UnauthorizedAccess = lazy(() => import('../screens/UnauthorizedAccess'));
const Roles = lazy(() => import('../screens/Main/Roles'));
const Permissions = lazy(() => import('../screens/Main/Permissions'));

const EmployeeList = lazy(() => import('../screens/Main/EmployeeList'));

const AddEmployee = lazy(() => import('../screens/Main/EmployeeList/AddEmployee'));
const AddEmployeeDetails = lazy(() => import('../screens/Main/EmployeeList/AddEmployeeDetails'));
const EmployeeDetails = lazy(() => import('../screens/Main/EmployeeList/EmployeeDetails'));
const SitesList = lazy(() => import('../screens/Main/Sites'));
const AssignSite = lazy(() => import('../screens/Main/Sites/AssignSite'));
const ViewSiteDetails = lazy(() => import('../screens/Main/Sites/ViewSiteDetails'));
const AddSite = lazy(() => import('../screens/Main/Sites/AddSite'));
const EditSite = lazy(() => import('../screens/Main/Sites/EditSite'));
const AddPatrol = lazy(() => import('../screens/Main/Patrol/AddPatrol'));
const EditPatrol = lazy(() => import('../screens/Main/Patrol/EditPatrol'));
const PatrolReportsList = lazy(() => import('../screens/Main/PatrolReports/PatrolReportsList'));
const PatrolReportsDetails = lazy(
  () => import('../screens/Main/PatrolReports/PatrolReportsDetails'),
);
const PatrolReportsDetails2 = lazy(
  () => import('../screens/Main/PatrolReports/PatrolReportsDetails2'),
);
const MyAccount = lazy(() => import('../screens/Main/MyAccount'));
const UpdatePassword = lazy(() => import('../screens/Main/MyAccount/UpdatePassword'));

const RoleProtectedRoute = ({ component: Component, requiredRole, ...rest }) => {
  const token = Cookies.get('token');
  const decodedToken = token ? jwtDecode(token) : null;
  const userRole = decodedToken ? decodedToken.role : null;

  return userRole === requiredRole ? <Component {...rest} /> : <UnauthorizedAccess />;
};

RoleProtectedRoute.propTypes = {
  component: PropTypes.elementType.isRequired,
  requiredRole: PropTypes.string.isRequired,
};

const PrivateRoutes = ({ as: Component, ...props }) => {
  const navigate = useNavigate();
  const user = Cookies.get('user');
  const token = Cookies.get('token');
  const isVerified = Cookies.get('verified');

  // start Ubaid Comment code 30/01/2024

  useEffect(() => {
    if (!user && !token && !isVerified) {
      navigate('/');
    }
  }, [user, token, isVerified, navigate]);

  // end Ubaid Comment code 30/01/2024

  return <Component {...props} />;
};

PrivateRoutes.propTypes = {
  as: PropTypes.elementType.isRequired,
};

const appNav = [
  {
    path: '/homepage',
    element: <HomePageV2 />,
  },
  {
    path: '/add-employee',
    element: <AddEmployee />,
  },
  {
    path: '/add-employee-details',
    element: <AddEmployeeDetails />,
  },
  {
    path: '/employee-list',
    element: <EmployeeList />,
  },
  {
    path: '/employee-details/:user_uuid',
    element: <EmployeeDetails />,
  },
  {
    path: '/sites-list',
    element: <SitesList />,
  },
  {
    path: '/assign-site',
    element: <AssignSite />,
  },
  {
    path: '/view-site-details/:siteUuid',
    element: <ViewSiteDetails />,
  },
  {
    path: '/add-site',
    element: <AddSite />,
  },

  {
    path: '/edit-site/:siteUuid',
    element: <EditSite />,
  },
  {
    path: '/add-patrol/:siteUuid',
    element: (
      <PatrolTaskProvider>
        <AddPatrol />
      </PatrolTaskProvider>
    ),
  },
  {
    path: '/edit-patrol/:siteUuid/:patrolTemplateUuid',
    element: (
      <PatrolTaskProvider>
        <EditPatrol />
      </PatrolTaskProvider>
    ),
  },
  {
    path: '/patrol-reports-list',
    element: (
      <PatrolReportProvider>
        <PatrolReportsList />,
      </PatrolReportProvider>
    ),
  },
  {
    path: '/patrol-reports-details/:patrolReportId',
    element: (
      <PatrolTaskProvider>
        <PatrolReportProvider>
          <PatrolReportsDetails />
        </PatrolReportProvider>
      </PatrolTaskProvider>
    ),
  },
  {
    path: '/patrol-reports-details',
    element: <PatrolReportsDetails2 />,
  },
  {
    path: '/my-account',
    element: <MyAccount />,
  },
  {
    path: '/update-password',
    element: <UpdatePassword />,
  },
  {
    path: '/notification-settings',
    element: <NotificationSettings />,
  },
  {
    path: '/search-results-list',
    element: <SearchResultsList />,
  },
  {
    path: '/admin-users',
    element: <RoleProtectedRoute component={AdminUsers} requiredRole="System Administrator" />,
  },
  {
    path: '/admin-agencies',
    element: <AdminAgencies />,
  },
  {
    path: '/expense',
    element: <Expense />,
  },
  {
    path: '/admin-location',
    element: <AdminLocation component={AdminLocation} requiredRole="admin" />,
  },
  {
    path: '/admin-party-types',
    element: <AdminPartyTypes />,
  },
  {
    path: '/deactivate-user',
    element: <AdminDeactivateUser />,
  },
  {
    path: '/activate-user',
    element: <RestoreAccount />,
  },
  {
    path: '/notificationlist',
    element: <NotificationList />,
  },
  {
    path: '/admin-casetype',
    element: <CaseType />,
  },
  {
    path: '/admin-parties',
    element: <Parties />,
  },
  {
    path: '/account-registration',
    element: <AccountRegistration />,
  },
  {
    path: '/time-entries',
    element: <TimeEntries />,
  },
  {
    path: '/rejected-time-entries',
    element: <RejectedTimeEntries />,
  },
  {
    path: '/role-management',
    element: <RoleManagement />,
  },
  {
    path: '/add-custom-role',
    element: <AddCustomRole />,
  },
  {
    path: '/system-role-detail',
    element: <SystemRoleDetail />,
  },
  {
    path: '/custom-role-detail',
    element: <CustomRoleDetail />,
  },
  {
    path: '/billing-period',
    element: <BillingPeriod />,
  },
  {
    path: '/edit-role-detail',
    element: <EditRoleDetail />,
  },
  {
    path: '/post-management',
    element: <RoleProtectedRoute component={PostManagementScreen} requiredRole="admin" />,
  },
  {
    path: '/roles',
    element: <RoleProtectedRoute component={Roles} requiredRole="admin" />,
  },
  {
    path: '/permissions',
    element: <RoleProtectedRoute component={Permissions} requiredRole="admin" />,
  },
  {
    path: '/signup',
    element: <Signup />,
  },
  {
    path: '/signup/:emailparam',
    element: <Signup />,
  },
];

const appRoutes = [
  {
    path: '/',
    element: <PrivateRoutes as={Main} />,
    children: appNav,
  },
];

export default appRoutes;
